import { useMemo, useState } from 'react';
import { crmStore, useCrm } from '../store';
import { Card, PageTitle, StatutBadge, Tag } from '../ui';
import { formatDateFR, formatEUR } from '../utils';
import { Search, User, Phone, Mail, Building2, Plus, Pencil, Trash2, MapPin, AlertTriangle, Music, Mic2, FileSpreadsheet, Archive, ChevronDown, ChevronRight } from 'lucide-react';
import ClientForm from '../components/ClientForm';
import { useConfirm } from '../components/ConfirmDialog';
import type { Client } from '../types';
import { toast } from 'sonner';
import { extractClientPrefs } from '../utils/clientPrefs';
import { exportXlsx } from '../utils/exportXlsx';

export default function Clients() {
  const data = useCrm();
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [editing, setEditing] = useState<Client | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [openHist, setOpenHist] = useState<Set<string>>(new Set());
  const toggleHist = (id: string) => setOpenHist((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const clients = data.clients || [];

  // Index prestations par client_id ET par nom (fallback)
  const { prestById, prestByNom } = useMemo(() => {
    const byId = new Map<string, typeof data.prest>();
    const byNom = new Map<string, typeof data.prest>();
    for (const p of data.prest as any[]) {
      if (p.clientId) {
        const arr = byId.get(p.clientId) ?? [];
        arr.push(p); byId.set(p.clientId, arr);
      }
      const key = (p.client || '').trim().toUpperCase();
      if (key) {
        const arr = byNom.get(key) ?? [];
        arr.push(p); byNom.set(key, arr);
      }
    }
    return { prestById: byId, prestByNom: byNom };
  }, [data.prest]);

  // Détection des conflits de date (≥2 prestations le même jour)
  const conflits = useMemo(() => {
    const byDate = new Map<string, typeof data.prest>();
    for (const p of data.prest) {
      if (!p.dateEvent || /Refus/i.test(p.statut)) continue;
      const arr = byDate.get(p.dateEvent) ?? [];
      arr.push(p);
      byDate.set(p.dateEvent, arr);
    }
    const set = new Set<string>();
    byDate.forEach((arr, d) => { if (arr.length >= 2) set.add(d); });
    return set;
  }, [data.prest]);

  const filtered = useMemo(() => {
    let arr = [...clients];
    if (typeFilter !== 'all') arr = arr.filter((c) => c.typeClient === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((c) =>
        [c.nom, c.prenom, c.societe, c.email, c.tel, c.ville, c.siret].filter(Boolean).join(' ').toLowerCase().includes(q)
      );
    }
    return arr.sort((a, b) => (a.societe || a.nom).localeCompare(b.societe || b.nom));
  }, [clients, search, typeFilter]);

  const isExcluCA = (p: any) => /^(Refus|Projet en pause|Expirée)/i.test(p.statut || '');
  const stats = useMemo(() => {
    const totalCA = data.prest.reduce((s, p) => s + (isExcluCA(p) ? 0 : (p.propTTC ?? 0)), 0);
    const pros = clients.filter((c) => c.typeClient !== 'particulier').length;
    return { total: clients.length, pros, events: data.prest.length, ca: totalCA, conflits: conflits.size };
  }, [clients, data.prest, conflits]);

  const openNew = () => { setEditing(null); setOpenForm(true); };
  const openEdit = (c: Client) => { setEditing(c); setOpenForm(true); };

  const handleDelete = async (c: Client) => {
    const ok = await confirm({
      title: 'Supprimer ce client ?',
      description: `${c.societe || c.nom} sera placé dans la corbeille.`,
      confirmLabel: 'Supprimer',
      destructive: true,
    });
    if (!ok) return;
    await crmStore.removeClient(c.id);
    toast.success('Client supprimé');
  };

  return (
    <div>
      <PageTitle
        title="Fichier Clients"
        subtitle={`${filtered.length} fiche(s) — agrégation des prestations liées`}
        icon={<User className="text-amber-400" size={24} strokeWidth={1.5} />}
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const stamp = new Date().toISOString().slice(0, 10);
                exportXlsx(`clients-${stamp}.xlsx`, filtered, [
                  { header: 'Type', key: 'typeClient' as any, width: 14 },
                  { header: 'Société', key: 'societe' as any, width: 28 },
                  { header: 'Nom', key: 'nom' as any, width: 22 },
                  { header: 'Prénom', key: 'prenom' as any, width: 16 },
                  { header: 'Fonction', key: 'fonction' as any, width: 20 },
                  { header: 'Email', key: 'email' as any, width: 28 },
                  { header: 'Téléphone', key: 'tel' as any, width: 16 },
                  { header: 'Tél. secondaire', key: 'telSecondaire' as any, width: 16 },
                  { header: 'Adresse', key: 'adresse' as any, width: 30 },
                  { header: 'CP', key: 'cp' as any, width: 8 },
                  { header: 'Ville', key: 'ville' as any, width: 20 },
                  { header: 'Département', key: 'departement' as any, width: 14 },
                  { header: 'Pays', key: 'pays' as any, width: 12 },
                  { header: 'SIRET', key: 'siret' as any, width: 18 },
                  { header: 'TVA Intra', key: 'tvaIntra' as any, width: 18 },
                  { header: 'Source', key: 'source' as any, width: 16 },
                  { header: 'Segment', key: 'segment' as any, width: 16 },
                  { header: 'Tags', key: (r: any) => (r.tags || []).join(', '), width: 22 },
                  { header: 'Potentiel business', key: 'potentielBusiness' as any, width: 16 },
                  { header: 'Notes', key: 'notes' as any, width: 40 },
                ], 'Clients');
              }}
              title="Exporter en Excel"
              className="px-3 py-2.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 font-medium rounded-lg text-sm transition-colors flex items-center gap-2"
            >
              <FileSpreadsheet size={14} strokeWidth={1.5} /> Excel
            </button>
            <button onClick={openNew} className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-medium rounded-lg text-sm flex items-center gap-2">
              <Plus size={16} strokeWidth={1.5} /> Nouveau client
            </button>
          </div>
        }
      />

      <ClientForm open={openForm} onOpenChange={setOpenForm} initial={editing} />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Clients</div><div className="font-heading text-2xl text-amber-400">{stats.total}</div></Card>
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Pro / B2B</div><div className="font-heading text-2xl text-violet-400">{stats.pros}</div></Card>
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Événements</div><div className="font-heading text-2xl text-sky-400">{stats.events}</div></Card>
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">CA Total</div><div className="font-heading text-2xl text-emerald-400">{formatEUR(stats.ca)}</div></Card>
        <Card className={`p-4 ${stats.conflits > 0 ? 'border-red-500/40 bg-red-500/5' : ''}`}>
          <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1">
            {stats.conflits > 0 && <AlertTriangle size={11} className="text-red-400" />} Conflits dates
          </div>
          <div className={`font-heading text-2xl ${stats.conflits > 0 ? 'text-red-400' : 'text-slate-400'}`}>{stats.conflits}</div>
        </Card>
      </div>

      {/* Filtres */}
      <Card className="p-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher (nom, société, email, ville, SIRET)…"
              className="w-full pl-9 pr-3 py-2 bg-slate-950/60 border border-slate-800 rounded-md text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <div className="flex gap-1.5">
            {[
              { v: 'all', l: 'Tous' },
              { v: 'particulier', l: 'Particuliers' },
              { v: 'entreprise', l: 'Entreprises' },
              { v: 'collectivite', l: 'Collectivités' },
              { v: 'association', l: 'Associations' },
            ].map((t) => (
              <button
                key={t.v}
                onClick={() => setTypeFilter(t.v)}
                className={`px-3 py-2 rounded-lg text-xs border ${typeFilter === t.v ? 'bg-amber-500/15 text-amber-400 border-amber-500/40' : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:bg-slate-800'}`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Liste */}
      <div className="space-y-4">
        {filtered.map((c) => {
          const key = (c.societe || c.nom).trim().toUpperCase();
          const allPrests = prestById.get(c.id) || prestByNom.get(key) || prestByNom.get((c.nom || '').trim().toUpperCase()) || [];
          const isPassif = (p: any) => !!p.archivedAt || /^(Refusée|Projet en pause|Expirée)$/i.test(p.statut || '');
          const prests = allPrests.filter((p: any) => !isPassif(p))
            .sort((a: any, b: any) => (a.dateEvent || '9999').localeCompare(b.dateEvent || '9999'));
          const archivedPrests = allPrests.filter((p: any) => isPassif(p))
            .sort((a: any, b: any) => (b.dateEvent || '').localeCompare(a.dateEvent || ''));
          const ca = allPrests.reduce((s, p) => s + (isExcluCA(p) ? 0 : (p.propTTC ?? 0)), 0);
          const isPro = c.typeClient !== 'particulier';
          const prefs = extractClientPrefs(c.tags);
          // Historique : domaines & artistes déjà sollicités via les prestations
          const histDom = Array.from(new Set(allPrests.flatMap((p: any) => p.domaines || []))).filter(Boolean) as string[];
          const histArt = Array.from(new Set(allPrests.flatMap((p: any) => p.artistes || []))).filter(Boolean) as string[];
          const histOpen = openHist.has(c.id);
          return (
            <Card key={c.id} className="overflow-hidden">
              <div className="p-4 border-b border-slate-800 flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {isPro ? <Building2 size={16} className="text-violet-400" strokeWidth={1.5} /> : <User size={16} className="text-sky-400" strokeWidth={1.5} />}
                    <div className="font-heading text-lg text-slate-100">{c.societe || `${c.prenom || ''} ${c.nom}`.trim()}</div>
                    {isPro && c.siret && <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-300 rounded text-[10px] font-mono">SIRET {c.siret}</span>}
                    {c.potentielBusiness && <Tag color="amber">{c.potentielBusiness}</Tag>}
                  </div>
                  {isPro && (c.prenom || c.nom) && (
                    <div className="text-xs text-slate-500">Contact : {[c.prenom, c.nom].filter(Boolean).join(' ')}{c.fonction ? ` — ${c.fonction}` : ''}</div>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs text-slate-400 mt-1.5">
                    {c.tel && <span className="flex items-center gap-1"><Phone size={11} strokeWidth={1.5} /> {c.tel}</span>}
                    {c.email && <span className="flex items-center gap-1"><Mail size={11} strokeWidth={1.5} /> {c.email}</span>}
                    {(c.ville || c.cp) && <span className="flex items-center gap-1"><MapPin size={11} strokeWidth={1.5} /> {[c.cp, c.ville].filter(Boolean).join(' ')}</span>}
                  </div>

                  {(prefs.domaines.length > 0 || histDom.length > 0) && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <Music size={11} className="text-amber-400" strokeWidth={1.5} />
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider mr-1">Domaines</span>
                      {prefs.domaines.map((d) => <Tag key={`pd-${d}`} color="amber">{d}</Tag>)}
                      {histDom.filter((d) => !prefs.domaines.includes(d)).map((d) => (
                        <Tag key={`hd-${d}`}>{d}<span className="text-[9px] text-slate-500 ml-1">hist.</span></Tag>
                      ))}
                    </div>
                  )}

                  {(prefs.artistes.length > 0 || histArt.length > 0) && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <Mic2 size={11} className="text-sky-400" strokeWidth={1.5} />
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider mr-1">Artistes</span>
                      {prefs.artistes.map((a) => <Tag key={`pa-${a}`} color="sky">{a}</Tag>)}
                      {histArt.filter((a) => !prefs.artistes.includes(a)).map((a) => (
                        <Tag key={`ha-${a}`}>{a}<span className="text-[9px] text-slate-500 ml-1">hist.</span></Tag>
                      ))}
                    </div>
                  )}

                  {c.notes && <div className="text-[11px] text-slate-500 italic mt-2 line-clamp-2">📝 {c.notes}</div>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">CA cumulé</div>
                    <div className="font-heading text-amber-400">{formatEUR(ca)}</div>
                    <div className="text-[10px] text-slate-500">{allPrests.length} évén.{archivedPrests.length > 0 && <> · <span className="text-slate-400">{archivedPrests.length} arch.</span></>}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-amber-400" title="Modifier"><Pencil size={14} strokeWidth={1.5} /></button>
                    <button onClick={() => handleDelete(c)} className="p-1.5 hover:bg-red-500/10 rounded text-slate-400 hover:text-red-400" title="Supprimer"><Trash2 size={14} strokeWidth={1.5} /></button>
                  </div>
                </div>
              </div>

              {prests.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-emerald-500/5 border-t border-emerald-500/20 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-medium uppercase tracking-wider border border-emerald-500/30">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                      Actifs ({prests.length})
                    </span>
                    <span className="text-[10px] text-slate-500">triés par date événement</span>
                  </div>
                  <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-slate-900/40 text-[10px] text-slate-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-2 text-left">Date évén.</th>
                        <th className="px-4 py-2 text-left">Statut</th>
                        <th className="px-4 py-2 text-left">Libellé</th>
                        <th className="px-4 py-2 text-left">Lieu</th>
                        <th className="px-4 py-2 text-right">Prop. TTC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prests.map((p) => {
                        const conflit = conflits.has(p.dateEvent);
                        return (
                          <tr key={p.id} className={`border-t border-slate-800/60 hover:bg-slate-800/30 ${conflit ? 'bg-red-500/5' : ''}`}>
                            <td className="px-4 py-2.5 text-xs text-slate-300 flex items-center gap-1.5">
                              {conflit && <AlertTriangle size={11} className="text-red-400" strokeWidth={1.5} />}
                              {p.dateEvent ? formatDateFR(p.dateEvent) : '—'}
                            </td>
                            <td className="px-4 py-2.5"><StatutBadge statut={p.statut} /></td>
                            <td className="px-4 py-2.5 text-xs text-slate-300">{p.libelle}</td>
                            <td className="px-4 py-2.5 text-xs text-slate-400">{p.lieu || '—'}</td>
                            <td className="px-4 py-2.5 text-xs text-right text-amber-400">{p.propTTC != null ? formatEUR(p.propTTC) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table></div>
                </div>
              )}

              {archivedPrests.length > 0 && (
                <div className="border-t border-slate-800/60">
                  <button
                    onClick={() => toggleHist(c.id)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-slate-400 hover:bg-slate-800/30"
                  >
                    <span className="flex items-center gap-2">
                      {histOpen ? <ChevronDown size={13} strokeWidth={1.5} /> : <ChevronRight size={13} strokeWidth={1.5} />}
                      <Archive size={13} className="text-slate-500" strokeWidth={1.5} />
                      Historique &amp; archives ({archivedPrests.length})
                    </span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">Refusée · Pause · Expirée</span>
                  </button>
                  {histOpen && (
                    <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
                      <thead className="bg-slate-900/40 text-[10px] text-slate-500 uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-2 text-left">Date évén.</th>
                          <th className="px-4 py-2 text-left">Statut</th>
                          <th className="px-4 py-2 text-left">Libellé</th>
                          <th className="px-4 py-2 text-left">Lieu</th>
                          <th className="px-4 py-2 text-left">Archivé le</th>
                          <th className="px-4 py-2 text-right">Prop. TTC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {archivedPrests.map((p: any) => (
                          <tr key={p.id} className="border-t border-slate-800/60 hover:bg-slate-800/30 opacity-80">
                            <td className="px-4 py-2.5 text-xs text-slate-400">{formatDateFR(p.dateEvent)}</td>
                            <td className="px-4 py-2.5"><StatutBadge statut={p.statut} /></td>
                            <td className="px-4 py-2.5 text-xs text-slate-400">{p.libelle}</td>
                            <td className="px-4 py-2.5 text-xs text-slate-500">{p.lieu || '—'}</td>
                            <td className="px-4 py-2.5 text-xs text-slate-500">{p.archivedAt ? formatDateFR(p.archivedAt) : '—'}</td>
                            <td className="px-4 py-2.5 text-xs text-right text-amber-400/80">{p.propTTC != null ? formatEUR(p.propTTC) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
        {!filtered.length && (
          <Card className="p-12 text-center text-slate-500">
            {search.trim() ? (
              <div className="space-y-3">
                <div>Aucun client trouvé pour «&nbsp;<span className="text-slate-300">{search.trim()}</span>&nbsp;».</div>
                <button onClick={() => setSearch('')} className="px-3 py-1.5 text-xs rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25">
                  Réinitialiser la recherche
                </button>
              </div>
            ) : (
              <>Aucun client. Créez le premier via le bouton ci-dessus.</>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
